import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ActivationStart, Router } from '@angular/router';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import {
  routerActions,
  seasonApiActions,
  seasonInfoComponentActions,
} from './season.actions';
import { SeasonApiClient } from '@pool-league/frontend-shared-data-api';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { seasonFeature } from './season.feature';
import { SeasonStatus } from '@pool-league/shared-data-api';
import { toError } from '@pool-league/error';

@Injectable()
export class SeasonEffects {
  readonly #actions$ = inject(Actions);
  readonly #router = inject(Router);
  readonly #seasonApiClient = inject(SeasonApiClient);
  readonly #store = inject(Store);

  readonly loadSeason = createEffect(() =>
    this.#router.events.pipe(
      filter((e): e is ActivationStart => e instanceof ActivationStart),
      map((e) => e.snapshot.params['seasonSlug']),
      distinctUntilChanged(),
      switchMap((seasonSlug) =>
        seasonSlug
          ? this.#loadSeason(seasonSlug)
          : of(routerActions.seasonClosed()),
      ),
    ),
  );

  #loadSeason(seasonSlug: string) {
    return this.#seasonApiClient.getBySlug(seasonSlug).pipe(
      map((season) => seasonApiActions.seasonLoaded({ season })),
      catchError((error) =>
        of(seasonApiActions.seasonLoadError({ seasonSlug, error })),
      ),
      startWith(routerActions.seasonOpened({ seasonSlug })),
    );
  }

  readonly transitionStatus = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        seasonInfoComponentActions.transitionStatus,
        routerActions.seasonClosed,
        routerActions.seasonOpened,
      ),
      concatLatestFrom(() => this.#store.select(seasonFeature.selectSeason)),
      switchMap(([action, season]) => {
        if (action.type !== seasonInfoComponentActions.transitionStatus.type) {
          // drop inflight requests if season view is closed
          // or if a new season is opened:
          return EMPTY;
        }
        if (
          !('status' in season.value) ||
          !('id' in season.value) ||
          !season.value.id
        ) {
          return of(
            seasonApiActions.transitionStatusError({
              error: new Error(
                'Unable to transition season status: No season data loaded.',
              ),
            }),
          );
        }
        if (season.value.status === SeasonStatus.COMPLETE) {
          return of(
            seasonApiActions.transitionStatusError({
              error: new Error(
                'Unable to transition season status: Season already complete.',
              ),
            }),
          );
        }
        const nextStatus =
          season.value.status === SeasonStatus.DRAFT
            ? SeasonStatus.ACTIVE
            : SeasonStatus.COMPLETE;

        return this.#seasonApiClient
          .setStatus(season.value.id, nextStatus)
          .pipe(
            map((season) => seasonApiActions.seasonStatusUpdated({ season })),
            catchError((error: unknown) =>
              of(
                seasonApiActions.transitionStatusError({
                  error: toError(error),
                }),
              ),
            ),
          );
      }),
    ),
  );
}
